/**
 * Locale-aware month label helper for the Preventative Maintenance timeline.
 *
 * Backend cells carry an ISO 8601 `periodStart` (timezone-tagged, UTC). The
 * frontend formats the visible label here so the dashboard reads in the
 * user's locale without round-tripping through the server — the same
 * convention used by the stakeholder weekday picker
 * (resources/js/Pages/Stakeholders/Maintainers/weekdays.js).
 *
 * Critically, the formatter is anchored to UTC so the displayed month
 * matches the backend's calendar interpretation regardless of where the
 * browser is. Without this, a 2026-01-01T00:00:00Z slot would render as
 * "Dec" for a user in Honolulu (UTC−10) because the browser would localise
 * to 2025-12-31 14:00.
 */

type MonthFormat = 'short' | 'long' | 'narrow' | 'numeric' | '2-digit';

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const formatterFor = (locale: string, month: MonthFormat): Intl.DateTimeFormat => {
    const cacheKey = `${locale}::${month}`;
    let formatter = formatterCache.get(cacheKey);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, { month, timeZone: 'UTC' });
        formatterCache.set(cacheKey, formatter);
    }
    return formatter;
};

/**
 * Format an ISO 8601 timestamp as a month label in the given locale,
 * interpreted in UTC so cell labels line up with the backend's slot
 * boundaries regardless of the browser's timezone.
 *
 * @example
 *   monthLabel('2026-05-25T00:00:00+00:00', 'en')  // 'May'
 *   monthLabel('2026-05-25T00:00:00+00:00', 'fr')  // 'mai'
 *   monthLabel('2026-05-25T00:00:00+00:00', 'ar')  // 'مايو'
 */
export const monthLabel = (iso: string, locale: string, format: MonthFormat = 'short'): string => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return formatterFor(locale, format).format(date);
};
